import React, { useEffect, useState } from 'react';
import { Box, Alert, Heading, Link, Text, Skeleton } from '@razorpay/blade/components';
import { useQuery } from 'react-query';
import api from 'api';
import { mapNewFlowSettingsToDisplay, mapOldFlowSettingsToDisplay } from './utils';
import { DisplaySetting } from './types';

interface FlexibleBenefitsSettingsSectionProps {
  orgId: string;
  editLink: string;
  isM3S2Enabled: boolean;
  isFBPDisabled: boolean;
}

const FlexibleBenefitsSettingsSection = ({
  orgId,
  editLink,
  isM3S2Enabled,
  isFBPDisabled,
}: FlexibleBenefitsSettingsSectionProps) => {
  const [displaySettings, setDisplaySettings] = useState<DisplaySetting[]>([]);

  const {
    data: newFlowData,
    isLoading: isNewFlowLoading,
    isError: isNewFlowError,
  } = useQuery({
    queryKey: ['flexibleBenefitsEffectiveSettings', orgId],
    queryFn: () =>
      api.flexibleBenefitComponents.queries.listEffectiveSettings({
        merchant_id: orgId,
      }),
    enabled: !!orgId && isM3S2Enabled,
    refetchOnWindowFocus: false,
  });

  const {
    data: oldFlowData,
    isLoading: isOldFlowLoading,
    isError: isOldFlowError,
  } = useQuery({
    queryKey: ['flexibleBenefitsSettings', orgId],
    queryFn: () => api.flexibleBenefits.getSettings(orgId),
    enabled: !!orgId && !isM3S2Enabled && !isFBPDisabled,
    refetchOnWindowFocus: false,
  });

  const isLoading = isM3S2Enabled ? isNewFlowLoading : isOldFlowLoading;
  const isError = isM3S2Enabled ? isNewFlowError : isOldFlowError;

  useEffect(() => {
    if (isM3S2Enabled) {
      if (newFlowData?.payrollListEffectiveSettings?.settings) {
        const settings = newFlowData.payrollListEffectiveSettings.settings;
        const mappedSettings = mapNewFlowSettingsToDisplay(settings);
        setDisplaySettings(mappedSettings);
      }
      return;
    }
    if (!isM3S2Enabled && !isFBPDisabled && oldFlowData) {
      const mappedSettings = mapOldFlowSettingsToDisplay(oldFlowData);
      setDisplaySettings(mappedSettings);
    }
  }, [isM3S2Enabled, isFBPDisabled, newFlowData, oldFlowData]);

  if (isLoading) {
    return (
      <Box marginBottom={'48px'}>
        <Skeleton height={'200px'} width={'100%'} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box marginBottom={'48px'}>
        <Alert
          color="negative"
          isDismissible={false}
          description="Failed to load flexible benefits settings. Please try again later."
        />
      </Box>
    );
  }

  return (
    <Box marginBottom={'48px'}>
      <Box
        paddingBottom="spacing.4"
        display="flex"
        justifyContent="space-between"
        paddingLeft={'spacing.0'}>
        <Heading weight="semibold" size="medium" color="surface.text.gray.normal">
          Flexible Benefit Plan Setup
        </Heading>
        <Link href={editLink}>Edit</Link>
      </Box>
      <Box
        display={'flex'}
        flexDirection={'column'}
        backgroundColor={'surface.background.gray.intense'}
        paddingY={'spacing.3'}
        paddingX={'spacing.0'}
        width="100%"
        borderColor={'surface.border.gray.subtle'}
        borderWidth={'thin'}>
        {displaySettings.map((setting, index) => (
          <Box
            key={index}
            flexDirection={'row'}
            display={'flex'}
            borderBottomColor={
              index < displaySettings.length - 1 ? 'surface.border.gray.subtle' : undefined
            }
            borderBottomWidth={index < displaySettings.length - 1 ? 'thin' : undefined}>
            <Box width={'30%'} padding={'spacing.4'} textAlign={'right'}>
              <Text color="surface.text.gray.muted">{setting.label}</Text>
            </Box>
            <Box
              width={'70%'}
              padding={'spacing.4'}
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={'center'}>
              <Text color="surface.text.gray.normal">{setting.value}</Text>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export { FlexibleBenefitsSettingsSection };
